// Post-OAuth `next` URL persistence.
//
// OAuth providers (Google One Tap, GitHub redirect, magic-link) strip
// arbitrary query params on the round-trip back. To preserve a `?next=`
// parameter from the original sign-in entry across the provider hop,
// we stash it in localStorage on the way out and consume it on the
// callback page. Used from signin/signup/reset-password (entry side)
// and callback/magic-callback (callback side).
//
// FA204/FA207 / frank trap — `KEY` (module-scope) and `_safePath` (a sibling
// export read bare by consumeOAuthNext) are both unresolvable once this
// file's exports are individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers; readerVoice.js precedent).
// Both are inlined self-contained in each consumer.

export const stashOAuthNext = () => {
  const KEY = 'post_oauth_next'
  if (typeof window === 'undefined') return
  try {
    const next = new URL(window.location.href).searchParams.get('next')
    if (!next) return
    // Never store an API-base override param (`apiUrl`, `channel`): they name
    // the API the bearer token goes to (postOAuthNext.overrideParams.test.js).
    const OVERRIDE_PARAMS = ['apiUrl', 'channel']
    let stored = next
    try {
      const u = new URL(next, window.location.origin)
      if (u.origin === window.location.origin) {
        for (const k of OVERRIDE_PARAMS) u.searchParams.delete(k)
        stored = u.pathname + u.search + u.hash
      }
    } catch (_) {
      /* unparseable — consume clamps it to '/' */
    }
    window.localStorage.setItem(KEY, stored)
  } catch (_) {
    /* URL parse can throw on file:// — non-fatal */
  }
}

export const consumeOAuthNext = (urlSearchParamsNext = null) => {
  const KEY = 'post_oauth_next'
  // Clamp a `?next` redirect target to a SAFE same-origin path. The
  // callback page feeds the consumed value straight into
  // `window.location.replace`, so an attacker-supplied absolute
  // (`https://evil.com`) or protocol-relative (`//evil.com`, `/\evil.com` —
  // browsers normalize `\` → `/`) value would be an open redirect
  // (phishing). Resolve against the current origin via the URL parser and
  // keep only same-origin requests, returning just the path; anything
  // cross-origin or unparseable falls back to `/`.
  const safePath = (next) => {
    if (!next || typeof next !== 'string') return '/'
    if (typeof window === 'undefined') {
      return next[0] === '/' && next[1] !== '/' && !next.includes('\\') ? next : '/'
    }
    try {
      const url = new URL(next, window.location.origin)
      if (url.origin !== window.location.origin) return '/'
      // never hand back an API-base override param (`apiUrl`, `channel`)
      for (const k of ['apiUrl', 'channel']) url.searchParams.delete(k)
      return url.pathname + url.search + url.hash
    } catch (_) {
      return '/'
    }
  }

  if (urlSearchParamsNext) return safePath(urlSearchParamsNext)
  if (typeof window === 'undefined') return '/'
  try {
    const stashed = window.localStorage.getItem(KEY)
    if (stashed) {
      window.localStorage.removeItem(KEY)
      return safePath(stashed)
    }
  } catch (_) {}
  return '/'
}
